import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchLiveTvChannels } from '@/store/slices/livetvSlice';
import { LiveTvChannelCard } from '@/components/LiveTvChannelCard';
import { LIVE_TV_CATEGORIES, type LiveTvCategory } from '@/types/livetv';
import './LiveTV.css';

type Filter = LiveTvCategory | 'all';

export function LiveTV(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const channels = useAppSelector((s) => s.livetv.channels);
  const status = useAppSelector((s) => s.livetv.status);
  const error = useAppSelector((s) => s.livetv.error);

  const [filter, setFilter] = useState<Filter>('all');
  const [country, setCountry] = useState<string>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (status === 'idle') void dispatch(fetchLiveTvChannels());
  }, [dispatch, status]);

  // Only render category chips that actually have channels.
  const presentCategories = useMemo(() => {
    const set = new Set(channels.map((c) => c.category));
    return LIVE_TV_CATEGORIES.filter((c) => set.has(c.id));
  }, [channels]);

  // Sorted list of countries present in the catalog — drives the country
  // filter. With the iptv-org source the catalog spans ~200 countries, so a
  // dropdown is the scalable control (category chips alone don't cut it).
  const presentCountries = useMemo(() => {
    const set = new Set<string>();
    for (const c of channels) if (c.country) set.add(c.country);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [channels]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return channels.filter((c) => {
      if (filter !== 'all' && c.category !== filter) return false;
      if (country !== 'all' && c.country !== country) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [channels, filter, country, query]);

  if (status === 'loading' && channels.length === 0) {
    return (
      <main className="livetv-page">
        <header className="livetv-page__header">
          <h1>{t('livetv.title')}</h1>
        </header>
        <div className="livetv-skeleton" role="status" aria-label={t('livetv.loadingLabel')}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="livetv-skeleton__tile" />
          ))}
        </div>
      </main>
    );
  }

  return (
    <main className="livetv-page">
      <header className="livetv-page__header">
        <h1>{t('livetv.title')}</h1>
        <p className="livetv-page__sub">{t('livetv.channelCount', { count: channels.length, formattedCount: String(channels.length) })}</p>
        {error && (
          <div role="alert" className="livetv-page__error">
            {error}{' '}
            <button type="button" onClick={() => dispatch(fetchLiveTvChannels())}>
              {t('common.retry')}
            </button>
          </div>
        )}
      </header>

      <div className="livetv-controls">
        <input
          type="search"
          className="livetv-search"
          placeholder={t('livetv.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t('livetv.searchLabel')}
        />
        <div className="livetv-chips" role="tablist" aria-label={t('livetv.filterByCategory')}>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all'}
            className={`livetv-chip${filter === 'all' ? ' livetv-chip--active' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('livetv.all')}
          </button>
          {presentCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={filter === c.id}
              className={`livetv-chip${filter === c.id ? ' livetv-chip--active' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {t(`livetv.categories.${c.id}`)}
            </button>
          ))}
        </div>
        {presentCountries.length > 1 && (
          <select
            className="livetv-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            aria-label={t('livetv.filterByCountry')}
          >
            <option value="all">{t('livetv.allCountries')}</option>
            {presentCountries.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="muted livetv-empty">
          {status === 'ready' ? t('livetv.noMatch') : t('livetv.loadingChannels')}
        </p>
      ) : (
        <div className="livetv-grid" data-testid="livetv-grid">
          {visible.map((channel) => (
            <LiveTvChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      )}
    </main>
  );
}
